import React, { useState } from "react";
import { Helmet } from "react-helmet";
import { Container } from "reactstrap";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";

import { Heading } from "components/Heading";
import { PageHeadings, SortOrder, PageTitles } from "../../../constants";
import typography from "../typography";
import Header from "./Header";
import DataTable from "../../../components/DataTable/DataTable";
import { truncateString } from "../../../utils/commonUtils";
import viewIcon from "../../../assets/images/app/common/aou-view-icon.svg";
import { Pagination } from "../../../components/Pagination";
import { APP_ROUTES } from "../../../helpers/routeHelpers";
import { ConfirmationModal } from "../../../components/CustomModal";
import editIcon from "../../../assets/images/app/common/pencil-icon.svg";
import deleteIcon from "../../../assets/images/app/common/trash-icon.svg";
import {
    getScheduledNotifications,
    deleteScheduledNotification,
} from "../../../api/api.service";
import { useLocalPaginatedRequest } from "../../../hooks/useLocalPaginatedRequest";
import { formatScheduleDateTime } from "../../../utils/dateFormatUtils";

const Listing = ({}) => {
    const history = useHistory();

    const [isModalActive, setIsModalActive] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState({});
    const [isDeleting, setIsDeleting] = useState(false);
    const [filters, setFilters] = useState({ status: 1 });

    const [currentSortColumn, setCurrentSortColumn] = useState(null);
    const [currentSortOrder, setCurrentSortOrder] = useState(null);

    const getStatusFilterValue = (filterValue) => {
        switch (filterValue) {
            case 2:
                return "SENT";
            case 3:
                return "PENDING";
            default:
                return null;
        }
    };

    const {
        isFetching,
        page,
        total,
        data,
        searchText,
        request: refetch,
        handlePageClick,
        onChangePageSize,
        pageSize,
        handleSearchChange,
        setPageToOne,
    } = useLocalPaginatedRequest(
        getScheduledNotifications,
        {
            ...(filters.status !== 1 && {
                status: getStatusFilterValue(filters.status),
            }),
            ...(currentSortColumn &&
                currentSortOrder && {
                    column: currentSortColumn,
                    direction: currentSortOrder,
                }),
        },
        [filters.status, currentSortOrder],
        "Q"
    );

    const handleSortClick = (item) => {
        setCurrentSortColumn(item.sortColumn);
        setCurrentSortOrder((prev) =>
            prev === SortOrder.ASC || !prev ? SortOrder.DESC : SortOrder.ASC
        );
    };

    const handleDetailsView = (id) => {
        history.push(`${APP_ROUTES.NOTIFICATION_DETAILS}/${id}`);
    };

    const handleEditNotification = (id) => {
        history.push(`${APP_ROUTES.EDIT_NOTIFICATION}/${id}`);
    };

    const handleDeleteNotification = (notificationData) => {
        setSelectedNotification(notificationData);
        setIsModalActive(true);
    };

    const confirmDelete = async () => {
        if (!selectedNotification?.id) {
            toast.error("Invalid notification selected");
            setIsModalActive(false);
            return;
        }

        try {
            setIsDeleting(true);
            const response = await deleteScheduledNotification(
                selectedNotification.id
            );

            if (response) {
                toast.success(
                    response.message || "Notification deleted successfully!"
                );

                refetch();
            }
        } catch (error) {
            console.error("Error deleting notification:", error);
        } finally {
            setIsModalActive(false);
            setIsDeleting(false);
        }
    };

    const handleAddNotification = () => {
        history.push(`${APP_ROUTES.ADD_NOTIFICATION}`);
    };

    const handleFilterChange = (value, type) => {
        setPageToOne();
        setFilters({ ...filters, [type]: value });
    };

    const getStatusBadgeClass = (status) => {
        return status === "SENT" ? "status-sent" : "status-pending";
    };

    return (
        <>
            <div className="page-content">
                <Helmet>
                    <title>{PageTitles.NOTIFICATION_MANAGEMENT}</title>
                </Helmet>
                <Container fluid className="bls-feedback customer-management">
                    <Heading heading={PageHeadings.NOTIFICATION_MANAGEMENT} />
                    <Header
                        statusFilterVal={filters.status}
                        filterUpdateHandler={handleFilterChange}
                        searchTextVal={searchText}
                        searchValUpdateHandler={handleSearchChange}
                        onAddNotification={handleAddNotification}
                    />
                    <DataTable
                        data={data}
                        loading={isFetching}
                        currentSortOrder={currentSortOrder}
                        currentSortColumn={currentSortColumn}
                        onSortClick={handleSortClick}
                        config={[
                            {
                                isSortable: true,
                                title: typography.TITLE,
                                sortColumn: "title",
                                render: (data) => (
                                    <div className="d-flex align-items-center">
                                        <span className="ms-2 standard-typo-14-400">
                                            {truncateString(data?.title, 30) ||
                                                "-"}
                                        </span>
                                    </div>
                                ),
                            },
                            {
                                isSortable: false,
                                title: typography.NOTIFICATION_MESSAGE,
                                render: (data) => (
                                    <div className="standard-typo-14-400">
                                        {truncateString(data?.message, 50) ||
                                            "-"}
                                    </div>
                                ),
                            },
                            {
                                isSortable: true,
                                title: typography.SCHEDULE_DATE,
                                sortColumn: "scheduleAt",
                                render: (data) => (
                                    <div className="standard-typo-14-400">
                                        {formatScheduleDateTime(
                                            data?.scheduleAt
                                        )?.date || "-"}
                                    </div>
                                ),
                            },
                            {
                                isSortable: false,
                                title: typography.SCHEDULE_TIME,
                                render: (data) => (
                                    <div className="standard-typo-14-400">
                                        {formatScheduleDateTime(
                                            data?.scheduleAt
                                        )?.time || "-"}
                                    </div>
                                ),
                            },
                            {
                                isSortable: false,
                                title: typography.STATUS,
                                render: (data) => (
                                    <div
                                        className={`notification-status-container ${getStatusBadgeClass(
                                            data?.status
                                        )}`}
                                    >
                                        <span>{data?.status}</span>
                                    </div>
                                ),
                            },
                            {
                                title: typography.ACTION,
                                render: (data) => {
                                    return (
                                        <div className="d-flex">
                                            <div
                                                className="view-form-icon-con"
                                                onClick={() =>
                                                    handleDetailsView(data.id)
                                                }
                                            >
                                                <img
                                                    src={viewIcon}
                                                    alt={"view icon"}
                                                    role="button"
                                                />
                                            </div>
                                            {data?.status === "PENDING" && (
                                                <div
                                                    className="view-form-icon-con"
                                                    onClick={() =>
                                                        handleEditNotification(
                                                            data.id
                                                        )
                                                    }
                                                >
                                                    <img
                                                        src={editIcon}
                                                        alt={"edit icon"}
                                                        role="button"
                                                    />
                                                </div>
                                            )}
                                            <div
                                                className="view-form-icon-con"
                                                onClick={() =>
                                                    handleDeleteNotification(
                                                        data
                                                    )
                                                }
                                            >
                                                <img
                                                    src={deleteIcon}
                                                    alt={"delete icon"}
                                                    role="button"
                                                />
                                            </div>
                                        </div>
                                    );
                                },
                            },
                        ]}
                    />
                    <Pagination
                        onPageClick={handlePageClick}
                        currentPage={page}
                        totalData={total}
                        handleChange={(e) => onChangePageSize(e)}
                        rowLimit={pageSize}
                    />
                </Container>
            </div>
            <ConfirmationModal
                isOpen={isModalActive}
                modalType="DELETE_NOTIFICATION"
                hasCustomConfig={true}
                heading="Are you sure you want to delete this notification?"
                headingClassName="delete-notification-heading"
                size="md"
                onNo={() => setIsModalActive(false)}
                onYes={confirmDelete}
                customYesBtnText={isDeleting ? "Deleting..." : "Delete"}
                customNoBtnText="Cancel"
                customImageSrc={deleteIcon}
                imageClassName="delete-notification-image"
                yesButtonClassName="delete-notification-yes-button"
                noButtonClassName="delete-notification-no-button"
                buttonsContainerClassName="delete-notification-buttons-container"
                disabled={isDeleting}
            />
        </>
    );
};

export default Listing;
